<script setup>
import IconCheckCircleGreen from '~icons/hawk/check-circle-green';
import IconWarn from '~icons/hawk/warn-icon';
import IconXCircleRed from '~icons/hawk/x-circle-red';
import { isEmpty } from 'lodash-es';
import { onMounted } from 'vue';
import { useModal } from 'vue-final-modal';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';

import DocumentUploadForm from '~/dms/components/documents/forms/document-upload-form.vue';

const props = defineProps({
  selectedDocuments: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['close', 'transmit', 'saveChanges']);

const {
  $t,
  $toast,
} = useCommonImports();

const form$ = ref(null);
const files_ref = ref([]);

const warning_message = computed(() => {
  const matched_count = files_ref.value.filter(file => matchFileNameToDocumentNumber(file)).length;
  const total_count = files_ref.value.length;

  if (matched_count === total_count)
    return null;

  return `${matched_count}/${total_count} documents are matched. ${matched_count === 0 ? 'All will be ignored' : `The remaining ${total_count - matched_count}`} will be ignored`;
});

const document_upload_modal = useModal({
  component: DocumentUploadForm,
});

function openSubmitModal() {
  const file_element = { name: 'File', component: 'MultifileElement', auto: false, addClass: { list: 'overflow-y-auto max-h-[50vh] scrollbar' } };
  const fields_list = ref([file_element]);

  document_upload_modal.patchOptions({
    attrs: {
      uploadLabel: $t('Add'),
      fields_list,
      is_single_file: false,
      submit: async (form) => {
        const files = form.data.File;

        files_ref.value.push(...files);
        document_upload_modal.close();
      },
      onClose() {
        document_upload_modal.close();
      },
    },
  });
  document_upload_modal.open();
}

function removeFile(index) {
  files_ref.value.splice(index, 1);
}

function matchFileNameToDocumentNumber(file) {
  const file_name = file.sanitized_name.split('.')[0];
  const selected_document_numbers = props.selectedDocuments.map(doc => doc.number);

  const same_name_files = files_ref.value.filter((file) => {
    return file.sanitized_name.split('.')[0] === file_name;
  });

  return same_name_files.length > 1 ? false : selected_document_numbers.includes(file_name);
}

function onTransmit() {
  const matched_files = files_ref.value.filter(file => matchFileNameToDocumentNumber(file)).map(file => ({ ...file, document_uid: props.selectedDocuments.find(doc => doc.number === file.sanitized_name.split('.')[0])?.uid }));

  const documents_payload = matched_files.map(file => ({
    uid: file.document_uid,
    name: file.sanitized_name,
    file_name: file.sanitized_name,
    size: file.file_size,
    type: file.mime_type,
    service_object: file.service_object,
  }));

  if (matched_files.length) {
    emit('transmit', { form_data: form$.value.data, documents_payload });
  }
  else {
    $toast({
      title: $t('No Files'),
      text: $t('File name does not matching with document number'),
      type: 'error',
    });
  }
}

function onSaveChanges() {
  const matched_files = files_ref.value.filter(file => matchFileNameToDocumentNumber(file)).map(file => ({ ...file, document_uid: props.selectedDocuments.find(doc => doc.number === file.sanitized_name.split('.')[0])?.uid }));

  const documents_payload = matched_files.map(file => ({
    uid: file.document_uid,
    attachment: {
      name: file.sanitized_name,
      file_name: file.sanitized_name,
      size: file.file_size,
      type: file.mime_type,
      service_object: file.service_object,
    },
  }));

  if (matched_files.length) {
    emit('saveChanges', { documents_payload });
  }
  else {
    $toast({
      title: $t('No Files'),
      text: $t('File name does not matching with document number'),
      type: 'error',
    });
  }
}

onMounted(() => {
  const draft_files = props.selectedDocuments.filter(doc => !isEmpty(doc.draft)).map(doc => ({
    ...doc.draft.attachment,
    sanitized_name: doc.draft.attachment.name,
    is_draft_file: true,
  }));

  if (draft_files.length) {
    files_ref.value.unshift(...draft_files);
  }

  if (!files_ref.value.length) {
    openSubmitModal();
  }
});
</script>

<template>
  <hawk-modal-container @click.stop>
    <Vueform
      ref="form$"
      size="sm"
      :display-errors="false"
      :columns="{
        default: { container: 12, label: 4, wrapper: 12 },
        sm: { container: 12, label: 4, wrapper: 12 },
        md: { container: 12, wrapper: 12, label: 4 },
      }"
    >
      <div class="col-span-12">
        <hawk-modal-header @close="emit('close')">
          <template #title>
            {{ $t('Bulk submit') }}
          </template>
        </hawk-modal-header>
        <!-- Body -->
        <hawk-modal-content class="!py-0">
          <div>
            <!-- File name match with Document number -->
            <div class="my-4">
              <div v-if="files_ref.length">
                <div class="flex items-center text-sm font-semibold text-gray-600 px-2 mb-4">
                  <span class="w-4/5">{{ $t('Uploaded file') }}</span>
                  <span class="w-1/5">{{ $t('Status') }}</span>
                </div>
                <!-- File list -->
                <div class="flex flex-col gap-2 scrollbar max-h-52 overflow-y-auto">
                  <template v-for="(file, index) in files_ref" :key="index">
                    <div class="flex items-center text-sm text-gray-900 p-2 rounded-md hover:bg-gray-50">
                      <span class="w-4/5 flex items-center gap-2">
                        <hawk-text :length="30" :content="file.sanitized_name" />
                        <hawk-badge v-if="file.is_draft_file" color="yellow">{{ $t('Draft') }}</hawk-badge>
                      </span>
                      <div class="flex w-1/5 justify-evenly">
                        <span class="">
                          <IconCheckCircleGreen v-if="matchFileNameToDocumentNumber(file)" />
                          <IconXCircleRed v-else />
                        </span>
                        <IconHawkTrashThree class="w-5 h-5 cursor-pointer" @click="() => removeFile(index)" />
                      </div>
                    </div>
                  </template>
                </div>
                <!-- Warning Message -->
                <div v-if="warning_message" class="flex p-4 border border-[#FEC84B] rounded-lg bg-[#FFFCF5] items-center my-4">
                  <div class="mr-2">
                    <IconWarn class="text-[#DC6803]" />
                  </div>
                  <div class="text-[#B54708] text-sm font-semibold">
                    {{ warning_message }}
                  </div>
                </div>
              </div>
              <div>
                <hawk-button type="outlined" @click="openSubmitModal">
                  <IconHawkPlus class="inline" />
                  <span class="text-gray-600">{{ $t("Add file") }}</span>
                </hawk-button>
              </div>
            </div>
            <!-- Extra attachments with comment -->
            <div class="my-2">
              <WysiwygEditorElement
                name="description"
                :label="$t('Comment')"
                :options="{
                  editor_enabled: true,
                  menu_enabled: true,
                  plugins: ['images', 'tables', 'tags'],
                  placeholder_text: $t('Enter info here'),
                }"
              />
              <DmsUploadElement
                class="mt-6"
                name="supporting_documents"
                :label="$t('Supporting documents')"
              />
            </div>
          </div>
        </hawk-modal-content>
        <hawk-modal-footer>
          <!-- Footer -->
          <template #right>
            <div class="flex">
              <div class="w-full">
                <HawkButton type="plain" :disabled="!files_ref.length" @click="onSaveChanges">
                  {{ $t('Save changes') }}
                </HawkButton>
              </div>
              <div class="flex justify-end w-full gap-4">
                <HawkButton type="outlined" @click="emit('close')">
                  {{ $t('Cancel') }}
                </HawkButton>
                <HawkButton :disabled="!files_ref.length" @click="onTransmit">
                  {{ $t('Transmit') }}
                </HawkButton>
              </div>
            </div>
          </template>
        </hawk-modal-footer>
      </div>
    </Vueform>
  </hawk-modal-container>
</template>
